// [Project] > the briefing's history: every version, named by when it was built and
// what built it, each opening the briefing as it stood then (read-only), with a link
// to the run that built it. See docs/Y2026-Q4/design-review-mode.md section 2.2.
import { Link, useParams } from "react-router-dom";

import { useProjectAgentStatus } from "../api";
import {
  type BriefingVersion,
  type ReviewComment,
  useBriefingVersions,
  useReviewComments,
} from "../api/hooks/useReview";
import { DeploymentTable } from "../components/DeploymentTable";
import { BriefingViewContext } from "../utils/briefingView";
import { formatWhen } from "../utils/formatWhen";
import {
  projectConversationPath,
  projectStatusHistoryPath,
  runPath,
} from "./learningAgentPaths";
import { LatestBriefing } from "./ProjectBriefingTab";
import { SpecialistDetails } from "./ProjectSpecialistTab";
import { StatusOverview } from "./ProjectStatusTab";
import styles from "./ProjectStatusTab.module.css";

const builtBy = (v: BriefingVersion): string => {
  const who = v.started_by ? ` with ${v.started_by}` : "";
  if (v.run_kind === "scheduled") return "Scheduled pass";
  if (v.run_kind === "chat" || v.run_kind === "agent_chat") return `Conversation${who}`;
  if (v.kinds.includes("decision_settled")) {
    return `Decision settled${v.started_by ? ` by ${v.started_by}` : ""}`;
  }
  return "Update outside a run";
};

/** Where the run that built a version is shown, or null when none built it. */
const runHref = (projectId: string, v: BriefingVersion): string | null => {
  if (!v.run_id) return null;
  if (v.run_kind === "chat") return projectConversationPath(projectId, v.run_id);
  if (v.run_kind === "scheduled") return runPath("scheduled", v.run_id);
  return runPath("conversations", v.run_id);
};

/** The review comments a version's run applied or settled. */
const reviewedBy = (comments: ReviewComment[], v: BriefingVersion): ReviewComment[] =>
  v.run_id
    ? comments.filter(
        (c) =>
          c.status !== "open" &&
          (c.settled_by_run_id === v.run_id || c.conversation_id === v.run_id),
      )
    : [];

export const ProjectStatusHistoryRoute = () => {
  const { projectId } = useParams();
  const decoded = projectId ? decodeURIComponent(projectId) : "";
  const { data, isLoading, error } = useBriefingVersions(decoded || null);
  const comments = useReviewComments(decoded ? { project_id: decoded } : null).data?.comments ?? [];
  if (isLoading) return <p className={styles.muted}>Loading the history…</p>;
  if (error) return <p className={styles.error}>Could not load this briefing's history.</p>;
  const versions = data?.versions ?? [];
  return (
    <div className={styles.tab}>
      <p className={styles.muted}>
        Every time the briefing was rebuilt, newest first.{" "}
        <Link to={`/projects/${encodeURIComponent(decoded)}/status`}>Back to the status</Link>
      </p>
      {versions.length === 0 ? (
        <p className={styles.muted}>Nothing has been built for this project yet.</p>
      ) : (
        <table className={styles.historyTable}>
          <thead>
            <tr>
              <th>Built</th>
              <th>By</th>
              <th>Reviews</th>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => {
              const run = runHref(decoded, v);
              const reviewed = reviewedBy(comments, v);
              return (
                <tr key={v.version}>
                  <td>{formatWhen(v.at)}</td>
                  <td>{builtBy(v)}</td>
                  <td>
                    {reviewed.length > 0 && (
                      <Link
                        to={projectConversationPath(decoded, reviewed[0].conversation_id ?? "")}
                      >
                        {reviewed.length} review comment{reviewed.length === 1 ? "" : "s"}{" "}
                        applied
                      </Link>
                    )}
                  </td>
                  <td>
                    <Link to={projectStatusHistoryPath(decoded, v.version)}>
                      View the briefing
                    </Link>
                  </td>
                  <td>
                    {run && (
                      <Link to={run}>
                        {v.run_kind === "scheduled" ? "Open the pass" : "Open the conversation"}
                      </Link>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

const RowAsBuilt = ({ projectId }: { projectId: string }) => {
  const version = Number(useParams().version);
  const { data } = useProjectAgentStatus(projectId, version);
  if (!data) return null;
  if (!data.dashboard_row || !data.attribute_definitions) {
    return (
      <p className={styles.muted}>
        No dashboard row was kept for this version: the row is kept as rendered only for
        versions built since that started.
      </p>
    );
  }
  return (
    <DeploymentTable
      attributes={data.attribute_definitions}
      rows={[data.dashboard_row]}
      linkNames={false}
      split
      sitePage
    />
  );
};

export const ProjectStatusVersionRoute = () => {
  const { projectId, version: raw } = useParams();
  const decoded = projectId ? decodeURIComponent(projectId) : "";
  const version = Number(raw);
  const versions = useBriefingVersions(decoded || null);
  const shown = versions.data?.versions.find((v) => v.version === version);
  const latest = versions.data?.latest ?? 0;
  const status = useProjectAgentStatus(decoded || null, version);
  const specialists = (status.data?.agents ?? []).filter((a) => a.keeps_status_row);
  return (
    <BriefingViewContext.Provider value={{ version, latest }}>
      <div className={styles.tab}>
        <p className={styles.historic}>
          The briefing as it was built
          {shown ? ` on ${formatWhen(shown.at)} by the ${builtBy(shown).toLowerCase()}` : ""}.
          Read-only.{" "}
          <Link to={projectStatusHistoryPath(decoded)}>All versions</Link>
          {" · "}
          <Link to={`/projects/${encodeURIComponent(decoded)}/status`}>The latest</Link>
        </p>
        <h3 className={styles.historyHeading}>Dashboard row</h3>
        <RowAsBuilt projectId={decoded} />
        <h3 className={styles.historyHeading}>Overview</h3>
        <StatusOverview projectId={decoded} />
        <h3 className={styles.historyHeading}>Next steps</h3>
        <LatestBriefing projectId={decoded} />
        {specialists.map((s) => (
          <section key={s.agent}>
            <h3 className={styles.historyHeading}>{s.label} status</h3>
            <SpecialistDetails projectId={decoded} agent={s.agent} ownPage={false} />
          </section>
        ))}
      </div>
    </BriefingViewContext.Provider>
  );
};
